<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props">
        <slot></slot>
      </div>
    </template>

    <v-card data-cy="dialog--">
      <v-card-title v-if="noSelectedItems" class="d-flex justify-space-between" data-cy="dialog--">
        <span v-if="action === 'archive'">{{ $t("routes.node.archiveNoSelectedItems") }}</span>
        <span v-else>{{ $t("routes.node.unarchiveNoSelectedItems") }}</span>

        <v-btn icon="mdi-close" @click="dialog = false" :aria-label="$t('common.close')" />
      </v-card-title>

      <v-card-title v-else class="d-flex justify-space-between" data-cy="dialog--">
        <span v-if="action === 'archive'">{{ $t("routes.node.archiveDialogTitle", totalItems) }}</span>
        <span v-else>{{ $t("routes.node.unarchiveDialogTitle", totalItems) }}</span>

        <v-btn icon="mdi-close" @click="dialog = false" :aria-label="$t('common.close')" />
      </v-card-title>

      <v-card-text class="pt-5 scrollable">
        <div v-if="waitingForFtpCheck" class="text-center pt-5">
          <v-progress-circular class="float-left" indeterminate color="primary" :aria-label="$t('routes.node.loading')"></v-progress-circular>
          <h3 class="pl-2 mt-2 float-left">{{ $t('routes.node.loading') }}</h3>
        </div>

        <template v-if="!waitingForFtpCheck">
        <div v-if="action === 'archive' && affectedFtpUsers.length > 0" class="tw-warning" style="opacity: 0.65">
          <v-icon>mdi-alert-box</v-icon>
          <span>{{ $t('routes.node.archiveFtpUsersWarning') }}</span>
          <ul class="mx-5 mt-1">
            <li v-for="group in affectedFtpUsersByNode" :key="group.nodeId">
              <router-link :to="'/node/' + group.nodeId">{{ group.nodeName }}</router-link>
              ({{ group.usernames.join(', ') }})
            </li>
          </ul>
        </div>

        <template v-else>
        <div v-if="unarchivePreviewMessage && !noSelectedItems">
          {{ $t("routes.node.unarchivePreviewMessage") }}
        </div>

        <div class="bg-success" v-if="successMessage">{{ successMessage }}</div>
        <div class="bg-error" v-if="errorMessage">{{ errorMessage }}</div>
        <div class="bg-error" v-if="errorTranslatable">
          {{ $replaceT(errorTranslatable.code, errorTranslatable.params, errorTranslatable.content) }}
        </div>

        <UtilityPodsErrors></UtilityPodsErrors>
        <UtilityNodesErrors></UtilityNodesErrors>

        <!-- Confirmation question -->
        <div v-if="!noSelectedItems" class="mb-4 text-h6">
          <span v-if="action === 'archive'">{{ $t("routes.node.archiveFolderConfirmQuestion", totalItems) }}</span>
          <span v-else>{{ $t("routes.node.unarchiveFolderConfirmQuestion", totalItems) }}</span>
        </div>

        <!-- Folder list -->
        <div v-if="archivableNodes.length > 0" class="mb-4">
          <div v-for="node in archivableNodes" :key="node.id" class="d-flex align-center py-1">
            <v-icon class="mr-2">mdi-folder</v-icon>
            <span class="text-body-1">{{ node.name }}</span>
          </div>
        </div>

        <!-- Archive explanation -->
        <div v-if="!noSelectedItems && action == 'archive'" class="mt-4">
          <div class="d-flex align-start mb-2">
            <v-icon class="mr-2" color="info">mdi-information-outline</v-icon>
            <span>{{ $t("routes.node.archiveExplanationAccessibility") }}</span>
          </div>
          <div class="d-flex align-start mb-2">
            <v-icon class="mr-2" color="info">mdi-information-outline</v-icon>
            <span>{{ $t("routes.node.archiveExplanationSpace") }}</span>
          </div>
          <div v-if="hasAnyRetention" class="d-flex align-start mb-2">
            <v-icon class="mr-2" color="info">mdi-information-outline</v-icon>
            <span>{{ $t("routes.node.archiveExplanationRetention") }}</span>
          </div>
        </div>

        <!-- Unarchive explanation -->
        <div v-if="!noSelectedItems && action == 'unarchive'" class="mt-4">
          <div class="d-flex align-start mb-2">
            <v-icon class="mr-2" color="info">mdi-information-outline</v-icon>
            <span>{{ $t("routes.node.unarchiveExplanationAccessibility") }}</span>
          </div>
          <div class="d-flex align-start mb-2">
            <v-icon class="mr-2" color="info">mdi-information-outline</v-icon>
            <span>{{ $t("routes.node.unarchiveExplanationSpace") }}</span>
          </div>
        </div>

        <div v-if="!noSelectedItems" class="mt-4 d-flex align-center">
          <v-icon class="mr-2" color="warning">mdi-clock-outline</v-icon>
          <span class="font-weight-medium text-warning">{{ $t("routes.node.taskCanTakeSomeTime") }}</span>
        </div>
        </template>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          v-if="!serviceHasRan && affectedFtpUsers.length === 0"
          @click="archiveSelected"
          :disabled="isOkDisabled"
          variant="flat"
          elevation="0"
          color="primary"
          data-cy="dialog--ok"
        >
          {{ getArchiveButtonString }}
        </v-btn>
        <v-btn variant="text" @click="dialog = false" data-cy="dialog--">{{ $t('common.cancel') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useServiceStore } from '@/stores/ServiceStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';

export default {
  mixins: [DialogActionKeys],
  name: 'ArchiveSelectedDialog',
  emits: ['update:modelValue', 'archiveSuccess'],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    action: {
      type: String,
      required: true,
    },
    nodes: {
      type: Array,
      required: false,
      default: () => [],
    },
    pods: {
      type: Array,
      required: false,
      default: () => [],
    },
    unarchivePreviewMessage: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      serviceHasRan: false,
      errorMessage: '',
      successMessage: '',
      errorTranslatable: null,
      okayPressed: false,
      waitingForFtpCheck: false,
      affectedFtpUsers: [],
    };
  },
  methods: {
    async updatePodsUtility() {
      if (!this.getArchiveService) {
        // this message should not be shown anyways...
        this.errorMessage = 'Archiving service not found.';
        return Promise.reject(new Error('Archiving service not found'));
      }

      const utilityPayload = {
        serviceAlias: this.getArchiveService.alias,
        pods: this.archivablePods,
        nodes: this.archivableNodes,
        action: this.action,
      };

      const serviceStore = useServiceStore();
      return serviceStore.updatePodsUtility(utilityPayload);
    },
    async archiveSelected() {
      const self = this;
      this.successMessage = '';
      this.errorMessage = '';
      this.errorTranslatable = null;
      this.okayPressed = true;

      try {
        await this.updatePodsUtility();

        const serviceStore = useServiceStore();
        const runnablePods = serviceStore.getRunnablePods;
        const runnableNodes = serviceStore.getRunnableNodes;

        if (runnableNodes.length === 0 && runnablePods.length === 0) {
          const errorMessage = self.action === 'archive'
            ? self.$t('routes.node.archiveOperationError')
            : self.$t('routes.node.unarchiveOperationError');

          throw {
            response: {
              data: {
                error: {
                  message: errorMessage,
                },
              },
            },
          };
        }

        const payload = {
          serviceAlias: self.getArchiveService.alias,
          utilityPods: runnablePods,
          utilityNodes: runnableNodes,
          params: {
            action: self.action,
          },
        };

        await serviceStore.runService(payload);

        if (self.utilityPodsErrors.length === 0 && self.utilityNodesErrors.length === 0) {
          self.successMessage = self.$t('routes.pod.menuSaveFrame.operationQueued');
          self.serviceHasRan = true;
          self.$emit('archiveSuccess', true);
          self.dialog = false;
        }
      } catch (error) {
        if (error?.response?.data?.error) {
          self.errorMessage = error.response.data.error.message;
        } else {
          self.errorMessage = 'Error!';
          console.log(error); // unhandled error
        }
      }
    },
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    archivableNodes() {
      const status = this.action === 'archive' ? 0 : 1;
      return (this.nodes || []).filter((x) => x.archived === status);
    },
    archivablePods() {
      const status = this.action === 'archive' ? 0 : 1;
      return (this.pods || []).filter((x) => x && x.archived === status);
    },
    totalItems() {
      return this.archivablePods.length + this.archivableNodes.length;
    },
    noSelectedItems() {
      const isNothingSelected = this.archivableNodes.length === 0 &&
        this.archivablePods.length === 0;

      return isNothingSelected;
    },
    isOkDisabled() {
      return this.noSelectedItems || this.okayPressed || this.waitingForFtpCheck
        || (this.action === 'archive' && this.affectedFtpUsers.length > 0);
    },
    getArchiveService() {
      const serviceStore = useServiceStore();
      return serviceStore.getArchiveService;
    },
    utilityPodsErrors() {
      const serviceStore = useServiceStore();
      return serviceStore.getUtilityPodsErrors;
    },
    utilityNodesErrors() {
      const serviceStore = useServiceStore();
      return serviceStore.getUtilityNodesErrors;
    },
    getArchiveButtonString() {
      if (this.action === 'archive') {
        return this.$t('common.archive');
      }

      return this.$t('common.unarchive');
    },
    hasAnyRetention() {
      return this.archivableNodes.some(node =>
        node.retention?.hasRetention ||
        node.retention?.hasParentWithRetention ||
        node.retention?.hasChildWithRetention
      );
    },
    affectedFtpUsersByNode() {
      const groups = {};
      this.affectedFtpUsers.forEach((u) => {
        if (!groups[u.nodeId]) {
          groups[u.nodeId] = { nodeId: u.nodeId, nodeName: u.nodeName, usernames: [] };
        }
        groups[u.nodeId].usernames.push(u.username);
      });
      return Object.values(groups);
    },
  },
  watch: {
    async dialog(newValue) {
      this.successMessage = '';
      this.errorMessage = '';
      this.errorTranslatable = null;
      this.affectedFtpUsers = [];

      if (newValue && this.action === 'archive' && this.archivableNodes.length > 0) {
        this.waitingForFtpCheck = true;
        try {
          const nodeStore = useNodeStore();
          const nodeIds = this.archivableNodes.map((n) => n.id);
          this.affectedFtpUsers = await nodeStore.getAffectedFtpUsers(nodeIds);
        } catch (e) {
          console.error('Failed to check FTP users:', e);
        } finally {
          this.waitingForFtpCheck = false;
        }
      }
    },
    archivablePods() {
      this.serviceHasRan = false;
      this.okayPressed = false;
    },
    archivableNodes() {
      this.serviceHasRan = false;
      this.okayPressed = false;
    },
  },
};
</script>
